import React, { useState, useEffect, useCallback } from 'react';
import { FormControl } from 'react-bootstrap';
import api from '../../services/api';
import debounce from 'lodash.debounce';

const Autocomplete = ({ value, onChange, placeholder, element_field_key, apiUrl, labelKey, valueKey }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);

  const fetchSuggestions = useCallback(
    debounce(async (input) => {
      if (!input || input.length < 2) {
        setSuggestions([]);
        return;
      }

      // Abort previous request
      if (abortController) {
        abortController.abort();
      }

      const newController = new AbortController();
      setAbortController(newController);

      try {
        setLoading(true);
        const response = await api.get(`${apiUrl}/?query=${input}`, {
          signal: newController.signal,
        });

        // Example response handling
        setSuggestions(response.data.result.list || []);
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Request was aborted');
        } else {
          console.error('Failed to fetch suggestions:', err);
        }
      } finally {
        setLoading(false);
      }
    }, 300), // Add a 300ms debounce
    [apiUrl, abortController]
  );

  const handleChange = (e) => {
    const inputValue = e.target.value;
    onChange({ target: { value: inputValue, code: '', name: element_field_key } });

    if (inputValue.length >= 2) {
      fetchSuggestions(inputValue);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelect = (suggestion) => {
    onChange({ target: { value: suggestion[labelKey], code: suggestion[valueKey], name: element_field_key } });
    setSuggestions([]);
  };

  useEffect(() => {
    // Cleanup on unmount or when a new controller is set
    return () => {
      fetchSuggestions.cancel();
      if (abortController) {
        abortController.abort();
      }
    };
  }, [fetchSuggestions, abortController]);

  return (
    <div className="autocomplete" style={{ position: 'relative' }}>
      <FormControl
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        element_field_key={element_field_key}
      />
      {loading && <div style={{ position: 'absolute', top: '100%', left: 0 }}>Loading...</div>}
      {suggestions.length > 0 && (
        <ul
          className="suggestions-list"
          style={{
            position: 'absolute',
            minWidth: '250px',
            listStyle: 'none',
            paddingLeft: '0',
            marginLeft: '5px',
            border: '1px solid #ccc',
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
            zIndex: '1',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSelect(suggestion)}
              className="suggestion-item"
              style={{
                padding: '10px',
                cursor: 'pointer',
                borderBottom: '1px solid #ddd',
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '';
              }}
            >
              {suggestion[labelKey]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Autocomplete;
